import React from 'react';
import L from 'leaflet';
import {
  AreaChart, Area, BarChart, Bar, CartesianGrid, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import MuiTooltip from '@material-ui/core/Tooltip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AddIcon from '@material-ui/icons/Add';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import LandscapeOutlinedIcon from '@material-ui/icons/LandscapeOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import SpeedIcon from '@material-ui/icons/Speed';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';

import Config from '../../constants/appConfig';
import ImportJobsDialog from './ImportJobsDialog';
import { normalizePreviewGeoJson } from './previewGeoJson';
import './analysis.css';

const COLORS = { navy: '#17324d', blue: '#2c78a8', orange: '#ee7d32', paleBlue: '#e8f2f8' };

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value, digits = 1) => numberValue(value).toLocaleString('pt-BR', {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits
});

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(numberValue(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}min`;
};

const formatDurationDifference = (seconds) => {
  const difference = Math.round(numberValue(seconds));
  const sign = difference > 0 ? '+' : (difference < 0 ? '-' : '');
  return `${sign}${formatDuration(Math.abs(difference))}`;
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString('pt-BR', {
  day: '2-digit', month: 'short', year: 'numeric'
}) : '-';

const comparisonVariation = (planned, executed, better = 'higher') => {
  if (planned === null || executed === null || planned === 0) return null;
  const variation = ((executed - planned) / planned) * 100;
  return better === 'lower' ? variation * -1 : variation;
};

const formatVariation = (variation) => {
  if (variation === null) return 'Sem comparativo';
  return `${variation > 0 ? '+' : ''}${formatNumber(variation)}%`;
};

class JobPreviewMap extends React.Component {
  mapNode = React.createRef();
  map = null;
  layers = null;

  componentDidMount() {
    this.map = L.map(this.mapNode.current, {
      zoomControl: true, attributionControl: false, preferCanvas: true
    }).setView(Config.place, 13);
    L.tileLayer(Config.tile_google, Config.tile_google_config).addTo(this.map);
    this.layers = L.featureGroup().addTo(this.map);
    this.drawData();
    window.setTimeout(() => {
      if (!this.map) return;
      this.map.invalidateSize();
      this.fitToLayers();
    }, 100);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) this.drawData();
  }

  componentWillUnmount() {
    if (this.map) this.map.remove();
  }

  addGeoJson = (geoJson, style) => {
    if (!geoJson || !this.layers) return;
    const normalized = normalizePreviewGeoJson(geoJson);
    if (!normalized) return;
    try { L.geoJSON(normalized, { style: style }).addTo(this.layers); } catch (error) { }
  };

  fitToLayers = () => {
    if (!this.map || !this.layers) return;
    const bounds = this.layers.getBounds();
    if (bounds.isValid()) this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
  };

  drawData = () => {
    if (!this.layers || !this.props.data) return;
    const data = this.props.data;
    this.layers.clearLayers();

    (data.polygons || []).forEach((item) => this.addGeoJson(item.poly || item, {
      color: COLORS.blue, weight: 2, fillColor: COLORS.paleBlue, fillOpacity: 0.2
    }));

    const applications = data.apl && Array.isArray(data.apl.aplicacao) ? data.apl.aplicacao : [];
    applications.forEach((item) => this.addGeoJson(item.poly || item, {
      color: COLORS.orange, weight: 1, fillColor: COLORS.orange, fillOpacity: 0.7
    }));

    this.addGeoJson(data.linha, { color: '#ffffff', weight: 5, opacity: 0.9 });
    this.addGeoJson(data.linha, { color: COLORS.navy, weight: 2, opacity: 1 });

    const base = data.position && Array.isArray(data.position.base) ? data.position.base : null;
    if (base && base.length >= 2) {
      L.circleMarker([base[0], base[1]], {
        radius: 6, color: '#ffffff', weight: 2, fillColor: COLORS.navy, fillOpacity: 1
      }).bindTooltip('Base operacional').addTo(this.layers);
    }

    this.fitToLayers();
  };

  render() {
    return <div ref={this.mapNode} className="job-yield-preview-map" aria-label="Mapa do trabalho executado" />;
  }
}

class YieldAnalysis extends React.Component {
  importPollTimer = null;

  constructor(props) {
    super(props);
    this.state = {
      titles: [], selectedTitle: '', titlesLoading: true,
      items: [], loading: true, error: '', expandedId: null,
      preview: null, previewLoading: false, previewError: '', importOpen: false,
      titleDialogOpen: false, newTitle: '', creatingTitle: false, titleError: '',
      pendingImports: []
    };
  }

  componentDidMount() { this.loadTitles(); }

  componentWillUnmount() {
    if (this.importPollTimer) window.clearTimeout(this.importPollTimer);
  }

  loadTitles = () => {
    this.setState({ titlesLoading: true, error: '' });
    fetch(`${Config.server}/job_view_title`, {
      method: 'GET', headers: { Authorization: localStorage.getItem('access-token-jwt') }
    })
      .then((response) => { if (!response.ok) throw response; return response.json(); })
      .then((titles) => {
        const availableTitles = Array.isArray(titles) ? titles : [];
        const selectedTitle = availableTitles.some((item) => (
          String(item.id) === String(this.state.selectedTitle)
        )) ? this.state.selectedTitle : (availableTitles[0] ? availableTitles[0].id : '');
        this.setState({
          titles: availableTitles, selectedTitle, titlesLoading: false,
          loading: Boolean(selectedTitle), items: selectedTitle ? this.state.items : []
        }, () => {
          if (selectedTitle) this.loadData();
        });
      })
      .catch((error) => {
        if (error && (error.status === 401 || error.status === 403)) return this.props.link('/login');
        this.setState({
          titlesLoading: false, loading: false,
          error: 'Nao foi possivel carregar os grupos de visualizacao.'
        });
      });
  };

  createTitle = () => {
    const title = String(this.state.newTitle || '').trim();
    if (!title) {
      this.setState({ titleError: 'Informe um nome para o grupo.' });
      return;
    }
    if (title.length > 99) {
      this.setState({ titleError: 'O nome do grupo deve ter no maximo 99 caracteres.' });
      return;
    }

    this.setState({ creatingTitle: true, titleError: '' });
    fetch(`${Config.server}/job_view_title`, {
      method: 'POST',
      headers: {
        Authorization: localStorage.getItem('access-token-jwt'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ title: title })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const requestError = new Error(data.message || 'Nao foi possivel criar o grupo.');
          requestError.status = response.status;
          throw requestError;
        }
        return data;
      })
      .then((createdTitle) => {
        this.setState((state) => ({
          titles: state.titles.concat(createdTitle).sort((first, second) => (
            String(first.title).localeCompare(String(second.title), 'pt-BR')
          )),
          selectedTitle: createdTitle.id,
          items: [],
          creatingTitle: false,
          titleDialogOpen: false,
          newTitle: '',
          titleError: ''
        }), () => this.loadData());
      })
      .catch((error) => {
        if (error && (error.status === 401 || error.status === 403)) {
          this.props.link('/login');
          return;
        }
        this.setState({
          creatingTitle: false,
          titleError: error && error.message
            ? error.message
            : 'Nao foi possivel criar o grupo.'
        });
      });
  };

  reconcilePendingImports = (pendingImports, items) => {
    if (!pendingImports.length) return [];
    const actualJobs = {};
    items.forEach((item) => { actualJobs[String(item.id_job)] = item; });
    const jobs = Object.keys(actualJobs).map((key) => actualJobs[key]);
    const usedJobs = new Set();

    return pendingImports.filter((pending) => {
      const status = String(pending.status || 'processing').toLowerCase();
      if (status === 'processing' || status === 'canceled') return true;

      const matchedByTask = pending.id_task && items.find((item) => {
        const itemTask = item.id_view_task
          || (item.job_link_type === 'processing' ? item.id_task : null);
        return itemTask && String(itemTask) === String(pending.id_task)
          && item.job_link_type !== 'processing';
      });
      if (matchedByTask) {
        usedJobs.add(String(matchedByTask.id_job));
        return false;
      }

      const candidates = jobs.filter((job) => (
        !pending.baselineIds.includes(String(job.id_job)) && !usedJobs.has(String(job.id_job))
      ));
      if (!candidates.length) return Date.now() - pending.created < 30 * 60 * 1000;

      const pendingTitle = String(pending.title || '').trim().toLowerCase();
      const matched = candidates.find((job) => String(job.title || '').trim().toLowerCase() === pendingTitle)
        || candidates[0];
      usedJobs.add(String(matched.id_job));
      return false;
    });
  };

  mergeServerPendingImports = (pendingImports, items) => {
    const merged = pendingImports.slice();
    const knownTasks = new Set(merged.filter((item) => item.id_task)
      .map((item) => String(item.id_task)));

    items.forEach((item) => {
      const isProcessing = item.job_link_type === 'processing'
        || Number(item.is_processing) === 1
        || String(item.task_status || '').toLowerCase() === 'processing';
      const idTask = item.id_view_task || item.id_task;
      if (!isProcessing || !idTask || knownTasks.has(String(idTask))) return;

      merged.push({
        key: `task-${idTask}`,
        id_task: idTask,
        title: item.title || `Trabalho ${idTask}`,
        created: item.created,
        progress: numberValue(item.progress),
        status: 'processing',
        baselineIds: []
      });
      knownTasks.add(String(idTask));
    });

    return merged;
  };

  scheduleImportRefresh = () => {
    if (this.importPollTimer) window.clearTimeout(this.importPollTimer);
    this.importPollTimer = null;
    const hasProcessing = this.state.pendingImports.some((item) => (
      item.id_task && String(item.status || 'processing').toLowerCase() === 'processing'
    ));
    if (hasProcessing) {
      this.importPollTimer = window.setTimeout(this.pollImportTasks, 3000);
      return;
    }

    const hasFinishedWaitingForData = this.state.pendingImports.some((item) => (
      String(item.status || '').toLowerCase() === 'finished'
      && String(item.id_title) === String(this.state.selectedTitle)
    ));
    if (hasFinishedWaitingForData) {
      this.importPollTimer = window.setTimeout(() => this.loadData(true), 3000);
    }
  };

  pollImportTasks = () => {
    if (this.importPollTimer) window.clearTimeout(this.importPollTimer);
    this.importPollTimer = null;
    const requestedIds = new Set(this.state.pendingImports.filter((item) => (
      item.id_task && String(item.status || 'processing').toLowerCase() === 'processing'
    )).map((item) => String(item.id_task)));
    if (!requestedIds.size) return;

    fetch(`${Config.server}/task/view`, {
      method: 'GET', headers: { Authorization: localStorage.getItem('access-token-jwt') }
    })
      .then((response) => { if (!response.ok) throw response; return response.json(); })
      .then((tasks) => {
        const taskList = Array.isArray(tasks) ? tasks : [];
        const tasksById = taskList.reduce((result, task) => {
          result[String(task.id)] = task;
          return result;
        }, {});
        let hasFinishedForSelectedTitle = false;

        this.setState((state) => ({
          pendingImports: state.pendingImports.map((item) => {
            if (!item.id_task || !requestedIds.has(String(item.id_task))) return item;
            const task = tasksById[String(item.id_task)];
            if (!task) return { ...item, status: 'canceled' };

            const status = String(task.status || 'processing').toLowerCase();
            if (status === 'finished'
              && String(item.id_title) === String(this.state.selectedTitle)) {
              hasFinishedForSelectedTitle = true;
            }
            return {
              ...item,
              title: task.title || item.title,
              progress: Math.max(0, Math.min(100, numberValue(task.progress))),
              status
            };
          })
        }), () => {
          const waitingFinishedData = this.state.pendingImports.some((item) => (
            String(item.status || '').toLowerCase() === 'finished'
            && String(item.id_title) === String(this.state.selectedTitle)
          ));
          if (hasFinishedForSelectedTitle || waitingFinishedData) this.loadData(true);
          else this.scheduleImportRefresh();
        });
      })
      .catch((error) => {
        if (error && (error.status === 401 || error.status === 403)) {
          this.props.link('/login');
          return;
        }
        this.scheduleImportRefresh();
      });
  };

  handleImported = (pendingItems, idTitle) => {
    const selectedTitle = idTitle || this.state.selectedTitle;
    const sameTitle = String(selectedTitle) === String(this.state.selectedTitle);
    const baselineIds = sameTitle ? Object.keys(this.state.items.reduce((ids, item) => {
      ids[String(item.id_job)] = true;
      return ids;
    }, {})) : [];
    const pendingImports = (pendingItems || []).map((item) => ({ ...item, baselineIds }));
    this.setState((state) => ({
      importOpen: false,
      selectedTitle,
      items: sameTitle ? state.items : [],
      pendingImports: state.pendingImports.concat(pendingImports)
    }), () => {
      this.loadData(true);
      this.scheduleImportRefresh();
    });
  };

  loadData = (background = false) => {
    const isBackground = background === true;
    const { selectedTitle } = this.state;
    if (!selectedTitle) {
      this.setState({ items: [], loading: false });
      return;
    }
    if (!isBackground) this.setState({ loading: true, error: '', expandedId: null, preview: null });
    const apiUrl = `${Config.server}/job_view/title/${selectedTitle}`;
    fetch(apiUrl, { method: 'GET', headers: { Authorization: localStorage.getItem('access-token-jwt') } })
      .then((response) => { if (!response.ok) throw response; return response.json(); })
      .then((items) => {
        const loadedItems = Array.isArray(items) ? items : [];
        this.setState((state) => {
          if (String(state.selectedTitle) !== String(selectedTitle)) return null;
          const matchingPending = state.pendingImports.filter((item) => (
            String(item.id_title) === String(selectedTitle)
          ));
          const otherPending = state.pendingImports.filter((item) => (
            String(item.id_title) !== String(selectedTitle)
          ));
          const reconciled = this.reconcilePendingImports(matchingPending, loadedItems);
          return {
            items: loadedItems,
            loading: false,
            pendingImports: otherPending.concat(this.mergeServerPendingImports(reconciled, loadedItems))
          };
        }, this.scheduleImportRefresh);
      })
      .catch((error) => {
        if (error && (error.status === 401 || error.status === 403)) return this.props.link('/login');
        this.setState({
          loading: false,
          error: isBackground ? this.state.error : 'Nao foi possivel carregar os dados do titulo selecionado.'
        }, this.scheduleImportRefresh);
      });
  };

  togglePreview = (item) => {
    if (this.state.expandedId === item.id_job) {
      this.setState({ expandedId: null, preview: null, previewError: '' });
      return;
    }
    this.setState({ expandedId: item.id_job, preview: null, previewLoading: true, previewError: '' });
    fetch(`${Config.server}/job_view/preview/${item.id}`, {
      method: 'GET', headers: { Authorization: localStorage.getItem('access-token-jwt') }
    })
      .then((response) => { if (!response.ok) throw response; return response.json(); })
      .then((preview) => this.setState({ preview, previewLoading: false }))
      .catch(() => this.setState({
        previewLoading: false,
        previewError: 'A pre-visualizacao deste trabalho nao esta disponivel.'
      }));
  };

  getExecutedItems = () => this.state.items.filter((item) => item.job_link_type === 'appraisal');

  getComparisonItems = (includePending = true) => {
    const jobs = {};
    const pendingImports = this.state.pendingImports.filter((item) => (
      String(item.id_title) === String(this.state.selectedTitle)
    ));
    const trackedTaskIds = new Set(pendingImports.filter((item) => item.id_task)
      .map((item) => String(item.id_task)));
    this.state.items.forEach((item) => {
      const itemViewTask = item.id_view_task || (item.job_link_type === 'processing' ? item.id_task : null);
      if (itemViewTask && trackedTaskIds.has(String(itemViewTask))) return;

      const key = String(item.id_job);
      const isProcessing = item.job_link_type === 'processing'
        || Number(item.is_processing) === 1
        || String(item.task_status || '').toLowerCase() === 'processing';
      const isCanceled = String(item.task_status || '').toLowerCase() === 'canceled';
      if (!jobs[key]) {
        jobs[key] = {
          id_job: item.id_job,
          title: item.title,
          created: item.created,
          planned: null,
          executed: null,
          pending: isProcessing,
          canceled: isCanceled,
          tracking: isCanceled,
          progress: numberValue(item.progress),
          id_task: itemViewTask
        };
      }
      if (isProcessing) jobs[key].pending = true;
      if (isCanceled) {
        jobs[key].canceled = true;
        jobs[key].tracking = true;
      }
      if (numberValue(item.progress) > numberValue(jobs[key].progress)) jobs[key].progress = numberValue(item.progress);
      if (!jobs[key].id_task && itemViewTask) jobs[key].id_task = itemViewTask;
      if (item.job_link_type === 'appraisal' && !jobs[key].executed) jobs[key].executed = item;
      if (item.job_link_type === 'planejamento' && !jobs[key].planned) jobs[key].planned = item;
      if (new Date(item.created) > new Date(jobs[key].created)) jobs[key].created = item.created;
    });

    const comparisons = Object.keys(jobs)
      .map((key) => jobs[key])
      .sort((a, b) => new Date(b.created) - new Date(a.created));
    if (!includePending) return comparisons;

    return pendingImports.map((item) => {
      const status = String(item.status || 'processing').toLowerCase();
      return {
        id_job: item.key,
        id_task: item.id_task,
        title: item.title,
        created: item.created,
        planned: null,
        executed: null,
        pending: status === 'processing',
        canceled: status === 'canceled',
        finished: status === 'finished',
        progress: numberValue(item.progress),
        tracking: true
      };
    }).concat(comparisons);
  };

  getTaskLabel = (item) => {
    if (item.canceled) return 'Cancelado';
    if (item.finished) return 'Concluido (100%)';
    return `Processando... ${Math.round(numberValue(item.progress))}%`;
  };

  getStats = () => {
    const comparisons = this.getComparisonItems(false).filter((item) => item.planned && item.executed);
    const executed = this.getExecutedItems();
    const area = executed.reduce((total, item) => total + numberValue(item.area_aplicada), 0);
    const average = (side, field) => comparisons.length
      ? comparisons.reduce((total, item) => total + numberValue(item[side][field]), 0) / comparisons.length
      : null;
    const plannedPerformance = average('planned', 'rendimento');
    const executedPerformance = average('executed', 'rendimento');
    const timeComparisons = comparisons.filter((item) => (
      numberValue(item.executed.tempo_total) >= numberValue(item.planned.tempo_total)
    ));
    const plannedTime = timeComparisons.length
      ? timeComparisons.reduce((sum, item) => sum + numberValue(item.planned.tempo_total), 0)
      : null;
    const executedTime = timeComparisons.length
      ? timeComparisons.reduce((sum, item) => sum + numberValue(item.executed.tempo_total), 0)
      : null;

    return {
      area,
      timeDifference: plannedTime === null || executedTime === null
        ? null
        : executedTime - plannedTime,
      performance: {
        planned: plannedPerformance,
        executed: executedPerformance,
        variation: comparisonVariation(plannedPerformance, executedPerformance, 'higher')
      },
      operationTime: {
        planned: plannedTime,
        executed: executedTime,
        variation: comparisonVariation(plannedTime, executedTime, 'lower')
      }
    };
  };

  getTrendData = () => this.getComparisonItems(false).slice().reverse().map((item) => ({
    name: new Date(item.created).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    Planejado: Number(numberValue(item.planned && item.planned.rendimento).toFixed(2)),
    Executado: Number(numberValue(item.executed && item.executed.rendimento).toFixed(2))
  }));

  getTimeData = () => {
    const average = (type, field) => {
      const filtered = this.state.items.filter((item) => item.job_link_type === type);
      if (!filtered.length) return 0;
      return Number((filtered.reduce((sum, item) => sum + numberValue(item[field]), 0) / filtered.length / 60).toFixed(1));
    };
    return [
      { name: 'Aplicacao', Executado: average('appraisal', 'tempo_aplicacao'), Planejado: average('planejamento', 'tempo_aplicacao') },
      { name: 'Translado', Executado: average('appraisal', 'tempo_translado'), Planejado: average('planejamento', 'tempo_translado') },
      { name: 'Balao', Executado: average('appraisal', 'tempo_balao'), Planejado: average('planejamento', 'tempo_balao') },
      { name: 'Solo', Executado: average('appraisal', 'tempo_solo'), Planejado: average('planejamento', 'tempo_solo') }
    ];
  };

  renderKpis = () => {
    const stats = this.getStats();
    const comparisonCard = (label, metric, formatter, icon) => ({
      label,
      value: formatVariation(metric.variation),
      valueClass: metric.variation !== null && metric.variation < 0 ? 'negative' : 'positive',
      comparison: {
        planned: metric.planned === null ? '-' : formatter(metric.planned),
        executed: metric.executed === null ? '-' : formatter(metric.executed)
      },
      icon
    });
    const cards = [
      {
        label: 'Tempo de operacao',
        value: stats.timeDifference === null
          ? 'Sem comparativo'
          : formatDurationDifference(stats.timeDifference * -1),
        valueClass: stats.timeDifference === null
          ? ''
          : (stats.timeDifference > 0 ? 'negative' : 'positive'),
        comparison: {
          planned: stats.operationTime.planned === null
            ? '-'
            : formatDuration(stats.operationTime.planned),
          executed: stats.operationTime.executed === null
            ? '-'
            : formatDuration(stats.operationTime.executed)
        },
        icon: <AccessTimeIcon />
      },
      comparisonCard(
        'Tempo de operacao',
        stats.operationTime,
        formatDuration,
        <AccessTimeIcon />
      ),
      comparisonCard(
        'Rendimento medio',
        stats.performance,
        (value) => `${formatNumber(value)} ha/h`,
        <SpeedIcon />
      ),
      { label: 'Area aplicada', value: `${formatNumber(stats.area)} ha`, detail: 'acumulado no titulo', icon: <LandscapeOutlinedIcon /> },
    ];
    return <div className="job-yield-kpis">{cards.map((card) => (
      <Paper className="job-yield-kpi" elevation={0} key={card.label}>
        <div className="job-yield-kpi-icon">{card.icon}</div>
        <div className="job-yield-kpi-content">
          <span>{card.label}</span>
          <strong className={card.valueClass || ''}>{card.value}</strong>
          {card.comparison ? <div className="job-yield-kpi-comparison">
            <span className="planned"><i />Planejado {card.comparison.planned}</span>
            <span className="executed"><i />Executado {card.comparison.executed}</span>
          </div> : <small>{card.detail}</small>}
        </div>
      </Paper>
    ))}</div>;
  };

  renderCharts = () => (
    <div className="job-yield-charts">
      <Paper className="job-yield-chart-card" elevation={0}>
        <div className="job-yield-section-title">
          <div><span>Rendimento por operacao</span><small>Comparativo planejado versus executado, em ha/h</small></div>
          <AssessmentOutlinedIcon />
        </div>
        <div className="job-yield-chart"><ResponsiveContainer width="100%" height="100%">
          <AreaChart data={this.getTrendData()} margin={{ top: 12, right: 10, left: -16, bottom: 0 }}>
            <defs><linearGradient id="areaApplied" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.35} />
              <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0.02} />
            </linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7edf2" />
            <XAxis dataKey="name" tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value) => [`${formatNumber(value)} ha/h`]} />
            <Legend />
            <Area type="monotone" dataKey="Planejado" stroke={COLORS.orange} strokeWidth={2} fill="transparent" />
            <Area type="monotone" dataKey="Executado" stroke={COLORS.blue} strokeWidth={2} fill="url(#areaApplied)" />
          </AreaChart>
        </ResponsiveContainer></div>
      </Paper>
      <Paper className="job-yield-chart-card" elevation={0}>
        <div className="job-yield-section-title">
          <div><span>Composicao do tempo</span><small>Media executada versus planejada, em minutos</small></div>
          <AccessTimeIcon />
        </div>
        <div className="job-yield-chart"><ResponsiveContainer width="100%" height="100%">
          <BarChart data={this.getTimeData()} margin={{ top: 12, right: 10, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7edf2" />
            <XAxis dataKey="name" tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#6d7f8e', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value) => [`${formatNumber(value)} min`]} /><Legend />
            <Bar dataKey="Planejado" fill={COLORS.orange} radius={[4, 4, 0, 0]} />
            <Bar dataKey="Executado" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer></div>
      </Paper>
    </div>
  );

  renderTimeDetails = (planned, executed) => {
    const data = [
      ['Aplicacao', 'tempo_aplicacao'], ['Translado', 'tempo_translado'],
      ['Balao', 'tempo_balao'], ['Solo', 'tempo_solo']
    ].map(([name, field]) => ({
      name,
      Executado: numberValue(executed && executed[field]) / 60,
      Planejado: numberValue(planned && planned[field]) / 60
    }));

    return <div className="job-yield-time-tooltip">
      <div className="job-yield-time-tooltip-title">
        <strong>Composicao do tempo</strong>
        <span>Planejado versus executado, em minutos</span>
      </div>
      <div className="job-yield-time-tooltip-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 12, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7edf2" />
            <XAxis dataKey="name" tick={{ fill: '#6d7f8e', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#6d7f8e', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value) => [`${formatNumber(value)} min`]} />
            <Legend wrapperStyle={{ color: '#536675', fontSize: 11 }} />
            <Bar dataKey="Planejado" fill={COLORS.orange} radius={[4, 4, 0, 0]} />
            <Bar dataKey="Executado" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>;
  };

  renderMetric = (planned, executed, field, formatter, options = {}) => {
    const plannedValue = planned ? numberValue(planned[field]) : null;
    const executedValue = executed ? numberValue(executed[field]) : null;
    // O percentual sempre representa ganho: rendimento maior e tempo menor
    // aparecem positivos; uma piora aparece negativa.
    const variation = comparisonVariation(plannedValue, executedValue, options.better);
    const isWorse = variation !== null && variation < 0;
    const variationElement = <small className={isWorse ? 'negative' : ''}>
      {formatVariation(variation)}
    </small>;

    const content = <div className="job-yield-comparison-cell">
      <span className="planned"><i />{plannedValue === null ? '-' : formatter(plannedValue)}</span>
      <span className="executed"><i />{executedValue === null ? '-' : formatter(executedValue)}</span>
      {options.showVariation !== false && variationElement}
    </div>;
    if (!options.timeDetails) return content;

    return <MuiTooltip
      title={this.renderTimeDetails(planned, executed)}
      arrow placement="top" interactive enterDelay={100} leaveDelay={120}
      PopperProps={{ className: 'job-yield-time-tooltip-popper' }}
    >
      <div className="job-yield-time-tooltip-trigger">{content}</div>
    </MuiTooltip>;
  };

  formatStrategy = (value) => {
    const names = {
      BACKTOBACK: 'Back to Back', SKIPBACKTOBACK: 'Skip Back to Back',
      RACETRACK: 'Racetrack', QUICKRACETRACK: 'Quick Racetrack',
      REVERSERACETRACK: 'Reverse Racetrack', EXPAND: 'Expand', SQUEEZE: 'Squeeze'
    };
    if (!value) return '-';
    return String(value).split(',').map((strategy) => {
      const normalized = strategy.trim().toUpperCase();
      return names[normalized] || strategy.trim();
    }).join(', ');
  };

  renderStrategy = (planned, executed) => <div className="job-yield-comparison-cell job-yield-strategy">
    <span className="planned"><i />{this.formatStrategy(planned && planned.estrategia)}</span>
    <span className="executed"><i />{this.formatStrategy(executed && executed.estrategia)}</span>
  </div>;

  renderPreview = (item) => (
    <TableRow className="job-yield-expanded-row"><TableCell colSpan={8}>
      <div className="job-yield-preview">
        <div className="job-yield-preview-head">
          <div><strong>Pre-visualizacao da operacao</strong><span>{item.title}</span></div>
          <div className="job-yield-map-legend">
            <span><i className="boundary" />Area do trabalho</span>
            <span><i className="application" />Faixa aplicada</span>
            <span><i className="path" />Trajetoria</span>
          </div>
        </div>
        {this.state.previewLoading && <div className="job-yield-preview-state"><CircularProgress size={28} /></div>}
        {this.state.previewError && <div className="job-yield-preview-state error">{this.state.previewError}</div>}
        {this.state.preview && <JobPreviewMap data={this.state.preview} />}
      </div>
    </TableCell></TableRow>
  );

  renderTable = () => (
    <Paper className="job-yield-table-card" elevation={0}>
      <div className="job-yield-section-title table-title">
        <div><span>Planejado x Executado</span><small>{this.getComparisonItems().length} trabalhos no titulo — clique em uma linha para visualizar o mapa</small></div>
        <div className="job-yield-comparison-legend">
          <span><i className="planned" />Planejado</span>
          <span><i className="executed" />Executado</span>
        </div>
      </div>
      <div className="job-yield-table-scroll"><Table className="job-yield-table">
        <TableHead><TableRow>
          <TableCell>Trabalho</TableCell><TableCell>Data / Previa</TableCell>
          <TableCell>Area</TableCell><TableCell>Tempo total</TableCell>
          <TableCell>Rendimento</TableCell><TableCell>Vel. media</TableCell>
          <TableCell>Estrategia</TableCell>
          <TableCell align="center">Acoes</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {this.getComparisonItems().map((item) => {
            const canPreview = item.executed && Number(item.executed.has_preview) === 1;
            const hasTaskState = item.tracking || item.pending;
            const taskLabel = this.getTaskLabel(item);
            return <React.Fragment key={item.id_job}>
              <TableRow
                hover={canPreview}
                className={`${this.state.expandedId === item.id_job ? 'selected' : ''} ${canPreview ? 'preview-enabled' : ''} ${item.pending ? 'processing' : ''} ${item.canceled ? 'canceled' : ''}`}
                onClick={() => { }/*canPreview && this.togglePreview(item.executed)*/}
              >
                <TableCell><strong className="job-yield-job-title">{item.title || `Trabalho #${item.id_job}`}</strong><small className={`job-yield-job-id ${item.pending ? 'processing' : ''} ${item.canceled ? 'canceled' : ''}`}>{hasTaskState ? taskLabel : `#${item.id_job}`}</small></TableCell>
                <TableCell>
                  <div className="job-yield-date-preview">
                    {item.pending ? <CircularProgress size={20} /> : <VisibilityOutlinedIcon fontSize="small" />}
                    <div><strong>{formatDate(item.created)}</strong><small>{hasTaskState ? taskLabel : (canPreview ? 'Clique para abrir o mapa' : 'Processamento indisponivel')}</small></div>
                  </div>
                </TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'area_aplicada', (value) => `${formatNumber(value)} ha`, { showVariation: false })}</TableCell>
                <TableCell className={hasTaskState ? '' : 'job-yield-time-total-cell'}>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'tempo_total', formatDuration, { better: 'lower', timeDetails: true })}</TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'rendimento', (value) => `${formatNumber(value)} ha/h`, { better: 'higher' })}</TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderMetric(item.planned, item.executed, 'velocidade_media', (value) => `${formatNumber(value)} km/h`, { showVariation: false })}</TableCell>
                <TableCell>{hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : this.renderStrategy(item.planned, item.executed)}</TableCell>
                <TableCell align="center"><div className="job-yield-actions">
                  {item.planned && item.planned.job_link_guid && <Button
                    component="a"
                    href={`/app/estimativa/edit/${item.planned.job_link_guid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    className="job-yield-link-button planned"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={(event) => event.stopPropagation()}
                  >Planejamento</Button>}
                  {item.executed && item.executed.job_link_guid && <Button
                    component="a"
                    href={`/app/avaliacao/${item.executed.job_link_guid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    className="job-yield-link-button"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={(event) => event.stopPropagation()}
                  >Avaliacao</Button>}
                  {!item.executed && !item.planned && (hasTaskState ? <span className={`job-yield-processing ${item.canceled ? 'canceled' : ''}`}>{taskLabel}</span> : '-')}
                </div></TableCell>
              </TableRow>
              {this.state.expandedId === item.id_job && this.renderPreview(item)}
            </React.Fragment>;
          })}
          {!this.state.loading && !this.getComparisonItems().length && <TableRow><TableCell colSpan={8} className="job-yield-empty">Nenhum registro encontrado para o titulo selecionado.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
      {this.state.loading && <div className="job-yield-loading"><CircularProgress size={30} /></div>}
      {this.state.error && <div className="job-yield-error">{this.state.error}</div>}
    </Paper>
  );

  render() {
    return <main className="job-yield-page">
      <section className="job-yield-hero">
        <div className="job-yield-heading">
          <h1>Analise de Rendimento</h1>
        </div>
        <div className="job-yield-hero-actions">
          <Button
            variant="outlined" className="job-yield-import-button"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => this.setState({ importOpen: true })}
          >Importar trabalhos</Button>
          <Paper className="job-yield-filter" elevation={0}>
            <label><span>Grupo da visualizacao</span><select
              value={this.state.selectedTitle}
              disabled={this.state.titlesLoading || !this.state.titles.length}
              onChange={(event) => this.setState({ selectedTitle: event.target.value }, () => this.loadData())}
            >
              {!this.state.titles.length && <option value="">Nenhum grupo disponivel</option>}
              {this.state.titles.map((title) => <option key={title.id} value={title.id}>{title.title}</option>)}
            </select></label>
            <MuiTooltip title="Adicionar grupo" arrow>
              <IconButton
                className="job-yield-add-title-button"
                aria-label="Adicionar grupo da visualizacao"
                onClick={() => this.setState({
                  titleDialogOpen: true, newTitle: '', titleError: ''
                })}
              ><AddIcon /></IconButton>
            </MuiTooltip>
            <Button variant="contained" className="job-yield-filter-button" startIcon={<RefreshIcon />} onClick={() => this.loadData()} disabled={this.state.loading || !this.state.selectedTitle}>Atualizar</Button>
          </Paper>
        </div>
      </section>
      {this.renderKpis()}{this.renderCharts()}{this.renderTable()}
      <ImportJobsDialog
        open={this.state.importOpen}
        selectedTitle={this.state.selectedTitle}
        onClose={() => this.setState({ importOpen: false })}
        onImported={this.handleImported}
        link={this.props.link}
      />
      <Dialog
        open={this.state.titleDialogOpen}
        onClose={this.state.creatingTitle ? undefined : () => this.setState({
          titleDialogOpen: false, newTitle: '', titleError: ''
        })}
        maxWidth="xs" fullWidth
        PaperProps={{ className: 'job-yield-import-dialog' }}
      >
        <DialogTitle className="job-yield-import-title" disableTypography>
          <div><AddIcon /></div>
          <div><strong>Adicionar grupo</strong><span>Crie um grupo para organizar a analise de rendimento</span></div>
        </DialogTitle>
        <DialogContent className="job-yield-import-content">
          <section className="job-yield-import-section">
            <div className="job-yield-import-section-title">
              <AssessmentOutlinedIcon />
              <div><strong>Grupo da visualizacao</strong><span>Informe um nome que identifique o agrupamento</span></div>
            </div>
            <TextField
              autoFocus fullWidth variant="outlined" label="Nome do grupo"
              value={this.state.newTitle}
              error={Boolean(this.state.titleError)}
              helperText={this.state.titleError || 'Maximo de 99 caracteres.'}
              disabled={this.state.creatingTitle}
              inputProps={{ maxLength: 99 }}
              onChange={(event) => this.setState({ newTitle: event.target.value, titleError: '' })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  this.createTitle();
                }
              }}
            />
          </section>
        </DialogContent>
        <DialogActions className="job-yield-import-actions">
          <Button
            className="job-yield-import-close"
            onClick={() => this.setState({
              titleDialogOpen: false, newTitle: '', titleError: ''
            })}
            disabled={this.state.creatingTitle}
          >Cancelar</Button>
          <Button
            className="job-yield-import-submit" variant="contained"
            onClick={this.createTitle}
            disabled={this.state.creatingTitle || !String(this.state.newTitle || '').trim()}
          >{this.state.creatingTitle ? <CircularProgress size={20} /> : 'Adicionar'}</Button>
        </DialogActions>
      </Dialog>
    </main>;
  }
}

export default YieldAnalysis;
